import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { RoundType } from './../enums/RoundType';
import { formatDateIso } from '@/utilities/dateutils';

export class EventSetup extends BaseObj {
    public ID: number;
    public __id: number;

    public AutoAssignEmitTags: boolean;
    public __autoassignemittags: boolean;

    public CategoryControlled: boolean;
    public __categorycontrolled: boolean;

    public EventID: number;
    public __eventid: number;

    public ISonenID: string | null;
    public __isonenid: string | null;

    public ISonenLastImport: Date | null;
    public __isonenlastimport: Date | null;

    public LiveEnabled: boolean;
    public __liveenabled: boolean;

    public RankResolution: number = 1;
    public __rankresolution: number = 1;

    public ResultResolution: number;
    public __resultresolution: number;

    public RoundType: RoundType;
    public __roundtype: RoundType;

    public UseManagerTimerecorder: boolean;
    public __usemanagertimerecorder: boolean;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AutoAssignEmitTags?: boolean;
        CategoryControlled?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        ISonenID?: string | null;
        ISonenLastImport?: Date | null;
        LiveEnabled?: boolean;
        RankResolution?: number;
        ResultResolution?: number;
        RoundType?: RoundType;
        UseManagerTimerecorder?: boolean;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventSetup>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventSetup tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AutoAssignEmitTags = values.AutoAssignEmitTags ?? false;
        this.__autoassignemittags = values.AutoAssignEmitTags ?? false;
        Object.defineProperty(this, 'AutoAssignEmitTags', {
            get(): boolean {
                return this.__autoassignemittags;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('AutoAssignEmitTags', this.__autoassignemittags, value);
                this.__autoassignemittags = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryControlled = values.CategoryControlled ?? false;
        this.__categorycontrolled = values.CategoryControlled ?? false;
        Object.defineProperty(this, 'CategoryControlled', {
            get(): boolean {
                return this.__categorycontrolled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('CategoryControlled', this.__categorycontrolled, value);
                this.__categorycontrolled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISonenID = values.ISonenID ?? null;
        this.__isonenid = values.ISonenID ?? null;
        Object.defineProperty(this, 'ISonenID', {
            get(): string | null {
                return this.__isonenid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ISonenID', this.__isonenid, value);
                this.__isonenid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISonenLastImport = values.ISonenLastImport ?? null;
        this.__isonenlastimport = values.ISonenLastImport ?? null;
        Object.defineProperty(this, 'ISonenLastImport', {
            get(): Date | null {
                return this.__isonenlastimport;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('ISonenLastImport', this.__isonenlastimport, value);
                this.__isonenlastimport = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LiveEnabled = values.LiveEnabled ?? false;
        this.__liveenabled = values.LiveEnabled ?? false;
        Object.defineProperty(this, 'LiveEnabled', {
            get(): boolean {
                return this.__liveenabled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LiveEnabled', this.__liveenabled, value);
                this.__liveenabled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RankResolution = values.RankResolution ?? 1;
        this.__rankresolution = values.RankResolution ?? 1;
        Object.defineProperty(this, 'RankResolution', {
            get(): number {
                return this.__rankresolution;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RankResolution', this.__rankresolution, value);
                this.__rankresolution = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResultResolution = values.ResultResolution ?? 0;
        this.__resultresolution = values.ResultResolution ?? 0;
        Object.defineProperty(this, 'ResultResolution', {
            get(): number {
                return this.__resultresolution;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ResultResolution', this.__resultresolution, value);
                this.__resultresolution = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RoundType = values.RoundType ?? 'Down';
        this.__roundtype = values.RoundType ?? 'Down';
        Object.defineProperty(this, 'RoundType', {
            get(): RoundType {
                return this.__roundtype;
            },
            set(value: RoundType) {
                this.__entityStateHandler.propertyChanged('RoundType', this.__roundtype, value);
                this.__roundtype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UseManagerTimerecorder = values.UseManagerTimerecorder ?? false;
        this.__usemanagertimerecorder = values.UseManagerTimerecorder ?? false;
        Object.defineProperty(this, 'UseManagerTimerecorder', {
            get(): boolean {
                return this.__usemanagertimerecorder;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('UseManagerTimerecorder', this.__usemanagertimerecorder, value);
                this.__usemanagertimerecorder = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AutoAssignEmitTags": ${JSON.stringify(this.AutoAssignEmitTags)}` +
            ',' +
            `"CategoryControlled": ${JSON.stringify(this.CategoryControlled)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"ISonenID": ${JSON.stringify(this.ISonenID)}` +
            ',' +
            `"ISonenLastImport": ${this.ISonenLastImport ? '"' + formatDateIso(this.ISonenLastImport) + '"' : null}` +
            ',' +
            `"LiveEnabled": ${JSON.stringify(this.LiveEnabled)}` +
            ',' +
            `"RankResolution": ${JSON.stringify(this.RankResolution)}` +
            ',' +
            `"ResultResolution": ${JSON.stringify(this.ResultResolution)}` +
            ',' +
            `"RoundType": ${this.RoundType == 'Down' ? 0 : this.RoundType == 'Round' ? 1 : this.RoundType == 'Up' ? 2 : 0}` +
            ',' +
            `"UseManagerTimerecorder": ${JSON.stringify(this.UseManagerTimerecorder)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventSetup from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AutoAssignEmitTags?: boolean;
        CategoryControlled?: boolean;
        EventID?: number;
        ISonenID?: string | null;
        ISonenLastImport?: Date | null;
        LiveEnabled?: boolean;
        RankResolution?: number;
        ResultResolution?: number;
        RoundType?: RoundType;
        UseManagerTimerecorder?: boolean;
        EntityState?: 'Added' | 'Unchanged';
    }): EventSetup {
        return new EventSetup(values);
    }

    public static _FromServerJson(json: any): EventSetup {
        return new EventSetup({
            ID: json.ID,
            AutoAssignEmitTags: json.AutoAssignEmitTags,
            CategoryControlled: json.CategoryControlled,
            EventID: json.EventID,
            ISonenID: json.ISonenID ?? '',
            ISonenLastImport: json.ISonenLastImport
                ? json.ISonenLastImport instanceof Date
                    ? json.ISonenLastImport
                    : new Date(json.ISonenLastImport)
                : null,
            LiveEnabled: json.LiveEnabled,
            RankResolution: json.RankResolution,
            ResultResolution: json.ResultResolution,
            RoundType: json.RoundType == 0 ? 'Down' : json.RoundType == 1 ? 'Round' : json.RoundType == 2 ? 'Up' : 'Down',
            UseManagerTimerecorder: json.UseManagerTimerecorder,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventSetup from another instance of EventSetup.
     * @param other The EventSetup you want to clone
     * @returns A cloned EventSetup
     */
    public static FromOther(other: EventSetup): EventSetup {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventSetup._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventSetup.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AutoAssignEmitTags'
            | 'CategoryControlled'
            | 'EventID'
            | 'ISonenID'
            | 'ISonenLastImport'
            | 'LiveEnabled'
            | 'RankResolution'
            | 'ResultResolution'
            | 'RoundType'
            | 'UseManagerTimerecorder'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventSetup';
}
